class R2PVariantPicker {
  constructor() {
    this.selectedOptions = {
      color: null,
      size: null
    };

    this.init();
  }

  init() {
    this.container = document.querySelector('.r2p-variant-selector');
    if (!this.container) return;

    this.productForm = document.querySelector('product-form')
      || document.querySelector('.js-product-form-main')
      || document.querySelector('form[id^="product-form"]');

    this.variantRadios = document.querySelector('variant-radios');
    this.variantSelects = document.querySelector('variant-selects');

    this.setupColorSwatches();
    this.bindEvents();
    this.initializeDefaultSelection();
  }

  initializeDefaultSelection() {
    const firstSizeRadio = this.container.querySelector('.size-radio:checked');
    if (firstSizeRadio) {
      this.selectedOptions.size = firstSizeRadio.value;
    }

    const firstSwatch = this.container.querySelector('.swatch-element');
    if (firstSwatch) {
      this.handleColorSelection(firstSwatch, true);
    }

    const variant = this.getSelectedVariant();
    if (variant) {
      this.triggerVariantChange(variant);
      this.updateMainProductImage(variant);
      this.updatePrice(variant);
    }
  }

  setupColorSwatches() {
    const colorSelector = this.container.querySelector('.variant-selector--color');
    if (!colorSelector) return;

    const colorVariants = this.getUniqueColorOptions();
    const swatchContainer = colorSelector.querySelector('.swatch-container');
    const variantLabel = colorSelector.querySelector('.variant-label');

    if (!swatchContainer || !variantLabel) return;

    // Debug: Log all colors to console
    console.log('[R2P Variant Picker] Found colors:', colorVariants);
    console.log('[R2P Variant Picker] Total variants:', window.productVariants?.length);
    console.log('[R2P Variant Picker] Color position:', this.getColorOptionPosition());

    if (!colorVariants.length) {
      console.warn('[R2P Variant Picker] No colors found!');
      return;
    }

    swatchContainer.innerHTML = '';

    colorVariants.forEach((color, index) => {
      const swatch = document.createElement('div');
      swatch.className = 'swatch-element';
      swatch.setAttribute('data-color', color);

      const tooltip = document.createElement('span');
      tooltip.className = 'swatch-tooltip';
      tooltip.textContent = color;
      swatch.appendChild(tooltip);

      const variantForColor = this.getVariantForColor(color);
      const variantImageSrc = variantForColor?.featured_image?.src;

      if (variantImageSrc) {
        const img = document.createElement('img');
        img.src = variantImageSrc;
        img.alt = color;
        img.loading = 'lazy';
        swatch.appendChild(img);
      } else {
        // Add placeholder if no image available
        swatch.style.backgroundColor = '#f3f4f6';
        swatch.style.display = 'flex';
        swatch.style.alignItems = 'center';
        swatch.style.justifyContent = 'center';
        const placeholder = document.createElement('span');
        placeholder.textContent = '?';
        placeholder.style.color = '#9ca3af';
        placeholder.style.fontSize = '20px';
        swatch.appendChild(placeholder);
      }

      swatchContainer.appendChild(swatch);

      if (index === 0) {
        this.handleColorSelection(swatch, true);
      }
    });
  }

  bindEvents() {
    this.container.addEventListener('click', (event) => {
      const swatch = event.target.closest('.swatch-element');
      if (swatch) {
        this.handleColorSelection(swatch);
      }

      const sizeLabel = event.target.closest('.size-label');
      if (sizeLabel) {
        this.handleSizeSelection(sizeLabel);
      }
    });
  }

  handleColorSelection(swatch, isInitial = false) {
    const color = swatch.getAttribute('data-color');
    this.selectedOptions.color = color;

    const colorSelector = this.container.querySelector('.variant-selector--color');
    const selectedColorSpan = colorSelector?.querySelector('.selected-color');
    if (selectedColorSpan) {
      selectedColorSpan.textContent = color;
    }

    this.container.querySelectorAll('.swatch-element').forEach((el) => {
      el.classList.remove('selected');
    });
    swatch.classList.add('selected');

    const variant = this.getSelectedVariant();
    if (!variant) return;

    if (!isInitial) {
      this.triggerVariantChange(variant);
      this.updateMainProductImage(variant);
      this.updatePrice(variant);
    } else if (!this.selectedOptions.size) {
      // Ensure default size selected to match variant
      const sizePos = this.getSizeOptionPosition();
      if (sizePos && variant) {
        const sizeKey = `option${sizePos}`;
        const defaultSize = variant[sizeKey];
        this.selectedOptions.size = defaultSize;
        const sizeRadio = this.container.querySelector(`.size-radio[value="${this.escapeSelector(defaultSize)}"]`);
        if (sizeRadio) sizeRadio.checked = true;
      }
    }
  }

  handleSizeSelection(label) {
    const radio = document.getElementById(label.getAttribute('for'));
    if (!radio) return;

    this.selectedOptions.size = radio.value;

    const variant = this.getSelectedVariant();
    if (variant) {
      this.triggerVariantChange(variant);
      this.updateMainProductImage(variant);
      this.updatePrice(variant);
    }
  }

  updateMainProductImage(variant) {
    if (!variant || !variant.featured_media) return;

    const mediaGallery = document.querySelector('media-gallery');
    if (!mediaGallery) return;

    const targetMediaItem = mediaGallery.querySelector(`[data-media-id="${variant.featured_media.id}"]`);

    if (targetMediaItem) {
      const galleryViewer = mediaGallery.querySelector('#gallery-viewer');
      if (galleryViewer) {
        galleryViewer.querySelectorAll('.media-viewer__item').forEach((item) => {
          item.classList.remove('is-current-variant');
        });

        targetMediaItem.classList.add('is-current-variant');
        this.safeGalleryScroll(targetMediaItem);
      }

      const mediaThumbsContainer = mediaGallery.querySelector('.media-thumbs');
      if (mediaThumbsContainer) {
        const matchingThumb = mediaThumbsContainer.querySelector(`[data-media-id="${variant.featured_media.id}"] button`);
        if (matchingThumb) {
          mediaThumbsContainer.querySelectorAll('.media-thumbs__btn').forEach((btn) => {
            btn.classList.remove('is-active');
            btn.removeAttribute('aria-current');
          });

          matchingThumb.classList.add('is-active');
          matchingThumb.setAttribute('aria-current', 'true');
          matchingThumb.click();
        }
      }
    }
  }

  updatePrice(variant) {
    if (!variant) return;

    const priceElement = document.querySelector('.product-info__price .price, .price');
    if (!priceElement) return;

    const priceCurrentEl = priceElement.querySelector('.price__current .js-value');
    const priceWasEl = priceElement.querySelector('.price__was .js-value');

    if (priceCurrentEl) {
      const formattedPrice = this.formatMoney(variant.price);
      priceCurrentEl.innerHTML = formattedPrice;

      if (priceWasEl && variant.compare_at_price && variant.compare_at_price > variant.price) {
        const formattedComparePrice = this.formatMoney(variant.compare_at_price);
        priceWasEl.innerHTML = formattedComparePrice;
        priceElement.classList.add('price--on-sale');
      } else {
        if (priceWasEl) priceWasEl.innerHTML = '';
        priceElement.classList.remove('price--on-sale');
      }

      priceElement.classList.toggle('price--sold-out', !variant.available);
    }
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = window.theme?.moneyFormat || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) return 0;

      const fixedNum = (number / 100.0).toFixed(precision);
      const parts = fixedNum.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    const formatMatch = formatString.match(placeholderRegex);
    if (!formatMatch) return cents;

    switch (formatMatch[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  triggerVariantChange(variant) {
    if (!variant) return;

    if (this.variantRadios) {
      const input = this.variantRadios.querySelector(`input[value="${variant.id}"]`);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (this.variantSelects) {
      const select = this.variantSelects.querySelector('select');
      if (select) {
        select.value = variant.id;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    if (this.productForm) {
      const input = this.productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (error) {
      console.warn('Could not update URL:', error);
    }

    this.container.dispatchEvent(new CustomEvent('on:variant:change', {
      detail: { variant },
      bubbles: true
    }));

    this.updatePrice(variant);
  }

  getColorOptionPosition() {
    const colorSelector = this.container.querySelector('.variant-selector--color');
    if (!colorSelector) return null;
    const position = colorSelector.getAttribute('data-option-position');
    return position ? parseInt(position) : null;
  }

  getSizeOptionPosition() {
    const sizeSelector = this.container.querySelector('.variant-selector--size');
    if (!sizeSelector) return null;
    const position = sizeSelector.getAttribute('data-option-position');
    return position ? parseInt(position) : null;
  }

  getUniqueColorOptions() {
    if (!window.productVariants || !window.productVariants.length) return [];
    const colorPos = this.getColorOptionPosition();
    if (!colorPos) return [];

    const optionKey = `option${colorPos}`;
    const options = window.productVariants
      .map((variant) => variant[optionKey])
      .filter(Boolean); // Filter empty/null values

    // Return unique values
    return [...new Set(options)];
  }

  getVariantForColor(color) {
    const colorPos = this.getColorOptionPosition();
    if (!colorPos) return null;
    const optionKey = `option${colorPos}`;
    
    // First find variant with image
    const variantWithImage = window.productVariants.find(
      (variant) => variant[optionKey] === color && variant.featured_image && variant.featured_image.src
    );
    
    // If no variant with image, return any variant with this color
    return variantWithImage || window.productVariants.find((variant) => variant[optionKey] === color);
  }

  getSelectedVariant() {
    if (!window.productVariants || !window.productVariants.length) return null;

    const colorPos = this.getColorOptionPosition();
    const sizePos = this.getSizeOptionPosition();

    const colorKey = colorPos ? `option${colorPos}` : null;
    const sizeKey = sizePos ? `option${sizePos}` : null;

    if (colorKey && sizeKey && this.selectedOptions.color && this.selectedOptions.size) {
      return window.productVariants.find((variant) => (
        variant[colorKey] === this.selectedOptions.color
        && variant[sizeKey] === this.selectedOptions.size
      ));
    }

    if (colorKey && this.selectedOptions.color) {
      return this.getVariantForColor(this.selectedOptions.color);
    }

    return window.productVariants[0];
  }

  escapeSelector(value) {
    if (!value) return '';
    if (window.CSS?.escape) {
      return CSS.escape(value);
    }
    return value
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\s/g, '\\ ');
  }

  safeGalleryScroll(element) {
    if (!element) return;

    const scrollContainer =
      element.closest('.media-gallery__viewer') ||
      element.closest('.media-gallery__slider') ||
      element.parentElement;

    if (!scrollContainer || scrollContainer === document.body) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = element.getBoundingClientRect();
    const overflowLeft = targetRect.left < containerRect.left;
    const overflowRight = targetRect.right > containerRect.right;

    if (overflowLeft || overflowRight) {
      const targetOffset =
        element.offsetLeft - scrollContainer.clientWidth / 2 + element.clientWidth / 2;

      scrollContainer.scrollTo({
        left: Math.max(0, targetOffset),
        behavior: 'smooth',
      });
    }
  }

}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.r2p-variant-selector')) {
    new R2PVariantPicker();
  }
});

